
import { useAuthStore } from "@/auth/authStore";
import { useAuth } from "@/auth/useAuth";
import { useThemeStore } from "@/shared/stores/themeStore";
import { useState } from "react";
import { ChangePasswordModal } from "@/features/organization";
import { RequestAssistButton } from "@/features/remoteAssist/components/RequestAssistButton";
import { TicketCaptureButton } from "@/features/support/components/TicketCaptureButton";
import { useNavigate } from "react-router-dom";
import headerSvg from "@/assets/brand/header.svg";

interface TopBarProps {
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (val: boolean) => void;
}

export function TopBar({ isSidebarCollapsed, setIsSidebarCollapsed }: TopBarProps) {
  const user = useAuthStore((s) => s.user);
  const { logout } = useAuth();
  const { theme, toggleTheme } = useThemeStore();
  const [isChangePwdOpen, setIsChangePwdOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <header
      className="glass-panel topbar-header"
      style={{
        height: "64px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 2rem",
        borderRadius: "0",
        borderBottom: "1px solid var(--color-border)",
        background: "var(--color-background)",
        color: "var(--color-text-main)",
        zIndex: 100,
        flexShrink: 0,
        position: "relative",
        overflow: "hidden"
      }}
    >
      <style>{`
        @media (max-width: 768px) {
          .topbar-header {
            padding: 0 0.75rem !important;
          }
          .topbar-brand-group {
            margin-right: 0 !important;
            gap: 0.6rem !important;
          }
          .topbar-brand-subtitle {
            display: none !important;
          }
          .topbar-brand-title {
            font-size: 1rem !important;
          }
          .topbar-user-info {
            display: none !important;
          }
        }
      `}</style>
      {/* TopBar SVG Background (header.svg) */}
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          backgroundImage: `url('${headerSvg}')`,
          backgroundSize: "auto auto",
          backgroundPosition: "right",
          backgroundRepeat: "no-repeat",
          opacity: 1,
          pointerEvents: "none",
          zIndex: -1
        }}
      />
      <div className="topbar-brand-group" style={{ display: "flex", alignItems: "center", gap: "1.2rem", marginRight: "3.0rem" }}>
        {/* Collapse toggle */}
        <button
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          aria-label={isSidebarCollapsed ? "باز کردن منوی ساندویچی" : "بستن منوی ساندویچی"}
          style={{
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            color: "var(--color-text-main)",
            borderRadius: "8px",
            width: "38px",
            height: "38px",
            cursor: "pointer",
            display: "flex",
            gap: "0.5rem",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.1rem",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = "var(--color-border)"}
          onMouseLeave={(e) => e.currentTarget.style.background = "var(--color-surface)"}
        >
          {isSidebarCollapsed ? "☰" : "✕"}
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
          <img
            src="/favicon.svg"
            alt="آیکون فرآیند میمزاد"
            style={{
              width: "36px",
              height: "36px",
              borderRadius: "8px",
              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.2)",
              display: "inline-block",
              flexShrink: 0,
            }}
          />
          <span className="topbar-brand-title" style={{ fontSize: "1.15rem", fontWeight: "800", letterSpacing: "0.5px", color: "var(--color-text-main)", fontFamily: "var(--font-family-fa)" }}>
            پلتفرم فرآیند میمزاد
          </span>
          <span className="topbar-brand-subtitle" style={{ fontSize: "0.95rem", fontWeight: "500", letterSpacing: "0.5px", color: "var(--color-text-muted)", fontFamily: "var(--font-family-fa)" }}>
            (Farayand - Manufacturing Platform by Mimzad)
          </span>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "1.2rem" }}>

        <button
          onClick={toggleTheme}
          title="تغییر حالت نمایش"
          style={{
            background: "var(--color-surface)",
            border: "1px solid var(--color-border)",
            color: "var(--color-text-main)",
            borderRadius: "8px",
            width: "38px",
            height: "38px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.1rem",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = "var(--color-border)"}
          onMouseLeave={(e) => e.currentTarget.style.background = "var(--color-surface)"}
        >
          {theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches) ? "☀️" : "🌙"}
        </button>

        <TicketCaptureButton />

        <div style={{ marginLeft: "1rem" }}>
          <RequestAssistButton onRequested={(sessionId) => navigate(`/remote-assist/${sessionId}`)} />
        </div>

        <div className="topbar-user-info" style={{ textAlign: "left", display: "flex", flexDirection: "column" }}>
          <span style={{ fontSize: "0.85rem", fontWeight: "700", color: "var(--color-text-main)" }}>{user?.username}</span>
          <span style={{ fontSize: "0.75rem", color: "var(--color-text-muted)" }}>{user?.email}</span>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            onClick={() => setIsChangePwdOpen(true)}
            style={{
              padding: "0.45rem 1rem",
              background: "rgba(37, 99, 235, 0.1)",
              border: "1px solid rgba(37, 99, 235, 0.25)",
              borderRadius: "8px",
              color: "#2563eb",
              fontSize: "0.85rem",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(37, 99, 235, 0.2)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(37, 99, 235, 0.1)";
            }}
          >
            تغییر رمز
          </button>

          <button
            onClick={() => void logout()}
            style={{
              padding: "0.45rem 1rem",
              background: "rgba(239, 68, 68, 0.1)",
              border: "1px solid rgba(239, 68, 68, 0.25)",
              borderRadius: "8px",
              color: "var(--color-error, #dc2626)",
              fontSize: "0.85rem",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "rgba(239, 68, 68, 0.2)";
              e.currentTarget.style.color = "#b91c1c";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(239, 68, 68, 0.1)";
              e.currentTarget.style.color = "var(--color-error, #dc2626)";
            }}
          >
            خروج
          </button>
        </div>
      </div>

      <ChangePasswordModal 
        open={isChangePwdOpen} 
        onClose={() => setIsChangePwdOpen(false)} 
      />
    </header>
  );
}
